import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import { getQueryOptions } from "@/lib/config/queryConfig";
import {
  getOperatorsForFieldtype,
  OPERATOR_LABELS,
  DATE_OPERATOR_LABELS,
} from "@/lib/erp/filterTypes";
import type {
  FilterDescriptor,
  FilterOperator,
  DocTypeSchema,
} from "@/lib/erp";
import { validateFilterArray } from "@/lib/validation/filterSchemas";
import { sanitizeFilterValue, sanitizeFieldName } from "@/lib/validation/sanitize";
import { logger } from "@/lib/utils/logger";
import type {} from "@/i18n/locales/en/inventory";

type TranslationModule = Record<string, string>;
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { 
  Trash2, 
  Plus, 
  Equal, 
  X, 
  Search, 
  SearchX, 
  ChevronRight, 
  ChevronLeft, 
  ChevronsRight, 
  ChevronsLeft, 
  ArrowLeftRight, 
  List, 
  ListX, 
  CheckCircle, 
  XCircle, 
  Circle,
  Calendar
} from "lucide-react";
import { LinkFieldSearch } from "./LinkFieldSearch";
import { AutocompleteFieldSearch } from "./AutocompleteFieldSearch";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";

// Operator icon and color mapping
const getOperatorIcon = (operator: FilterOperator) => {
  const iconMap: Record<FilterOperator, { icon: React.ComponentType<{ className?: string }>, color: string }> = {
    equals: { icon: Equal, color: "bg-[#007AFF]" },
    notEquals: { icon: X, color: "bg-[#FF3B30]" },
    like: { icon: Search, color: "bg-[#34C759]" },
    notLike: { icon: SearchX, color: "bg-[#FF9500]" },
    in: { icon: List, color: "bg-[#5856D6]" },
    notIn: { icon: ListX, color: "bg-[#FF2D55]" },
    greaterThan: { icon: ChevronRight, color: "bg-[#007AFF]" },
    lessThan: { icon: ChevronLeft, color: "bg-[#007AFF]" },
    greaterOrEqual: { icon: ChevronsRight, color: "bg-[#007AFF]" },
    lessOrEqual: { icon: ChevronsLeft, color: "bg-[#007AFF]" },
    between: { icon: ArrowLeftRight, color: "bg-[#AF52DE]" },
    isSet: { icon: CheckCircle, color: "bg-[#34C759]" },
    isNotSet: { icon: XCircle, color: "bg-[#FF3B30]" },
    is: { icon: Circle, color: "bg-[#8E8E93]" },
    timespan: { icon: Calendar, color: "bg-[#007AFF]" },
  };
  return iconMap[operator] || { icon: Equal, color: "bg-[#007AFF]" };
};

type FilterBuilderProps = {
  doctype: string;
  value: FilterDescriptor[];
  onChange: (filters: FilterDescriptor[]) => void;
  showAddButton?: boolean;
  translations?: TranslationModule;
};

export function FilterBuilder({
  doctype,
  value,
  onChange,
  showAddButton = true,
  translations,
}: FilterBuilderProps) {
  const { data: schema, isLoading, error: schemaError } = useQuery<DocTypeSchema>({
    queryKey: ["doctype-schema", doctype],
    queryFn: () => erp.meta.getDocTypeSchema(doctype),
    ...getQueryOptions("metadata"),
  });

  // Get filterable fields: all non-hidden fields (not just in_list_view)
  // Memoize to prevent re-filtering on every render
  const filterableFields = useMemo(() => {
    if (!schema?.fields) return [];
    return schema.fields.filter(
      (f) => f.fieldname && !f.hidden && f.fieldname !== "name"
    );
  }, [schema?.fields]);

  const addFilter = () => {
    const firstField = filterableFields[0];
    if (!firstField) return;

    onChange([
      ...value,
      {
        field: firstField.fieldname,
        operator: getOperatorsForFieldtype(firstField.fieldtype)[0],
        value: null,
      },
    ]);
  };

  const removeFilter = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateFilter = (
    index: number,
    updates: Partial<FilterDescriptor>
  ) => {
    const newFilters = [...value];
    const updatedFilter = { ...newFilters[index], ...updates };
    
    // Sanitize field name if provided
    if (updates.field) {
      updatedFilter.field = sanitizeFieldName(updates.field);
    }
    
    // Sanitize value if provided
    if (updates.value !== undefined) {
      updatedFilter.value = sanitizeFilterValue(updates.value) as FilterDescriptor["value"];
    }
    
    newFilters[index] = updatedFilter;
    
    // Validate filters before calling onChange
    const validation = validateFilterArray(newFilters);
    if (!validation.success) {
      logger.warn("Invalid filter update:", validation.error?.issues);
      // Still update to allow user to fix, but log the error
    }
    
    // ✅ Fix 4: Debug log to confirm FilterDescriptor format
    console.debug("🔍 FilterBuilder onChange", JSON.stringify(newFilters, null, 2));
    
    onChange(newFilters);
  };

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">
        Loading filter options...
      </div>
    );
  }

  if (schemaError) {
    return (
      <div className="text-sm text-destructive">
        Failed to load DocType schema: {schemaError instanceof Error ? schemaError.message : "Unknown error"}
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="text-sm text-destructive">
        Failed to load DocType schema
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {value.map((filter, index) => (
        <FilterRow
          key={index}
          doctype={doctype}
          filter={filter}
          fields={filterableFields}
          onChange={(updates) => updateFilter(index, updates)}
          onRemove={() => removeFilter(index)}
        />
      ))}
      {showAddButton && (
        <Button 
          onClick={addFilter} 
          variant="outline" 
          size="default"
          className="h-14 px-4 text-sm font-normal rounded-md"
        >
          <Plus className="size-4 mr-2" />
          {translations?.addFilter || "Add Filter"}
        </Button>
      )}
    </div>
  );
}

function FilterRow({
  doctype,
  filter,
  fields,
  onChange,
  onRemove,
}: {
  doctype: string;
  filter: FilterDescriptor;
  fields: DocTypeSchema["fields"];
  onChange: (updates: Partial<FilterDescriptor>) => void;
  onRemove: () => void;
}) {
  const currentField = fields.find((f) => f.fieldname === filter.field);
  const operators = currentField
    ? getOperatorsForFieldtype(currentField.fieldtype)
    : [];

  return (
    <div className="flex gap-2 items-center">
      {/* Field Select */}
      <Select
        value={filter.field}
        onValueChange={(field) => {
          const newField = fields.find((f) => f.fieldname === field);
          onChange({
            field,
            operator: newField
              ? getOperatorsForFieldtype(newField.fieldtype)[0]
              : "equals",
            value: null,
          });
        }}
      >
        <SelectTrigger className="w-[200px] h-14 px-4 text-sm font-normal rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {fields.map((f) => (
            <SelectItem key={f.fieldname} value={f.fieldname}>
              {f.label || f.fieldname}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator Select */}
      <Select
        value={filter.operator}
        onValueChange={(op) => onChange({ operator: op as FilterOperator })}
      >
        <SelectTrigger className="min-w-[150px] w-auto h-14 px-4 text-sm font-normal rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <div className="flex items-center gap-2 w-full whitespace-nowrap">
            {(() => {
              const { icon: Icon, color } = getOperatorIcon(filter.operator);
              const label =
                currentField?.fieldtype === "Date" ||
                currentField?.fieldtype === "Datetime"
                  ? DATE_OPERATOR_LABELS[filter.operator] || OPERATOR_LABELS[filter.operator]
                  : OPERATOR_LABELS[filter.operator];
              return (
                <>
                  <div className={cn("w-6 h-6 rounded-xs flex items-center justify-center shrink-0", color)}>
                    <Icon className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-left shrink-0">{label}</span>
                </>
              );
            })()}
          </div>
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => {
            const label =
              currentField?.fieldtype === "Date" ||
              currentField?.fieldtype === "Datetime"
                ? DATE_OPERATOR_LABELS[op] || OPERATOR_LABELS[op]
                : OPERATOR_LABELS[op];
            const { icon: Icon, color } = getOperatorIcon(op);
            return (
              <SelectItem key={op} value={op}>
                <div className="flex items-center gap-2">
                  <div className={cn("w-6 h-6 rounded-xs flex items-center justify-center", color)}>
                    <Icon className="w-3.5 h-3.5 text-white" />
                  </div>
                  {label}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {/* Value Input */}
      <FilterValueInput
        doctype={doctype}
        filter={filter}
        field={currentField}
        onChange={(value) => onChange({ value })}
      />

      <Button onClick={onRemove} variant="ghost" size="icon">
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

function FilterValueInput({
  doctype,
  filter,
  field,
  onChange,
}: {
  doctype: string;
  filter: FilterDescriptor;
  field?: DocTypeSchema["fields"][0];
  onChange: (value: FilterDescriptor["value"]) => void;
}) {
  if (!field) return null;

  // isSet/isNotSet/is - no value input
  if (
    filter.operator === "isSet" ||
    filter.operator === "isNotSet" ||
    filter.operator === "is"
  ) {
    return (
      <div className="text-sm text-muted-foreground w-[200px]">
        No value needed
      </div>
    );
  }

  // between - two inputs
  if (filter.operator === "between") {
    const [from, to] = (filter.value as [any, any]) || [null, null];
    const isDate = field.fieldtype === "Date" || field.fieldtype === "Datetime";

    return (
      <div className="flex gap-2">
        {isDate ? (
          <>
            <DatePicker
              date={from ? new Date(from) : undefined}
              onDateChange={(date) =>
                onChange([
                  date?.toISOString().split("T")[0] || null,
                  to,
                ])
              }
            />
            <DatePicker
              date={to ? new Date(to) : undefined}
              onDateChange={(date) =>
                onChange([
                  from,
                  date?.toISOString().split("T")[0] || null,
                ])
              }
            />
          </>
        ) : (
          <>
            <Input
              type="number"
              value={from || ""}
              onChange={(e) =>
                onChange([
                  e.target.value ? Number(e.target.value) : null,
                  to,
                ])
              }
              placeholder="From"
              className="w-[100px]"
            />
            <Input
              type="number"
              value={to || ""}
              onChange={(e) =>
                onChange([
                  from,
                  e.target.value ? Number(e.target.value) : null,
                ])
              }
              placeholder="To"
              className="w-[100px]"
            />
          </>
        )}
      </div>
    );
  }

  // in/notIn - multi-select (simplified for Phase 1, can enhance later)
  if (filter.operator === "in" || filter.operator === "notIn") {
    // For Phase 1: comma-separated input (can upgrade to proper multi-select later)
    return (
      <Input
        value={
          Array.isArray(filter.value)
            ? filter.value.join(", ")
            : String(filter.value || "")
        }
        onChange={(e) => {
          const val = e.target.value;
          onChange(
            val
              ? val
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : null
          );
        }}
        placeholder="Comma-separated values"
        className="w-[200px]"
      />
    );
  }

  // Check - boolean dropdown
  if (field.fieldtype === "Check") {
    return (
      <Select
        value={String(filter.value ?? "")}
        onValueChange={(v) => onChange(v === "true")}
      >
        <SelectTrigger className="w-[120px] h-14 px-4 text-sm font-normal rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  // Select - dropdown from options
  if (field.fieldtype === "Select" && field.options) {
    const options = field.options.split("\n").filter(Boolean);
    return (
      <Select
        value={String(filter.value ?? "")}
        onValueChange={(v) => onChange(v)}
      >
        <SelectTrigger className="w-[200px] h-14 px-4 text-sm font-normal rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Link - async searchable select
  if (field.fieldtype === "Link" && field.options) {
    // Only apply filters where we are absolutely sure about the schema
    const getDefaultFilters = (doctype: string): Record<string, any> | undefined => {
      // Only apply filters where we are absolutely sure about the schema
      if (doctype === "User") {
        // Users use "enabled" instead of "disabled"
        return { enabled: 1 };
      }

      // For all other doctypes, do NOT assume a "disabled" column exists.
      // Let Frappe handle visibility, or configure per-doctype explicitly later.
      return undefined;
    };
    
    return (
      <LinkFieldSearch
        doctype={field.options}
        value={filter.value as string | null}
        onChange={(v) => {
          // ✅ Fix 3: Debug log to confirm value is string
          console.debug("[FilterBuilder] LinkFieldSearch onChange", {
            field: filter.field,
            value: v,
            valueType: typeof v,
            isString: typeof v === "string",
          });
          
          // ✅ Ensure we're passing a string, not an object
          if (v !== null && typeof v !== "string") {
            console.error("[FilterBuilder] ERROR: LinkFieldSearch onChange received non-string!", v);
            return;
          }
          
          onChange(v);
        }}
        placeholder={`Search ${field.label}...`}
        fetchInitialItems={true} // ✅ Fix 2: Enable initial suggestions on click
        defaultFilters={getDefaultFilters(field.options)} // ✅ Fix: Pass appropriate filters per doctype
      />
    );
  }

  // Date/Datetime - date picker
  if (field.fieldtype === "Date" || field.fieldtype === "Datetime") {
    return (
      <DatePicker
        date={
          filter.value ? new Date(filter.value as string) : undefined
        }
        onDateChange={(date) =>
          onChange(date?.toISOString().split("T")[0] || null)
        }
      />
    );
  }

  // Data/Text fields - use autocomplete for like/equals operators
  // This provides server-side search suggestions like ERPNext
  if (
    (field.fieldtype === "Data" ||
      field.fieldtype === "Text" ||
      field.fieldtype === "Small Text" ||
      field.fieldtype === "Long Text") &&
    (filter.operator === "like" ||
      filter.operator === "notLike" ||
      filter.operator === "equals" ||
      filter.operator === "notEquals")
  ) {
    return (
      <AutocompleteFieldSearch
        doctype={doctype}
        fieldname={field.fieldname}
        value={filter.value as string | null}
        onChange={(v) => onChange(v)}
        placeholder={`Search ${field.label}...`}
        operator={filter.operator}
      />
    );
  }

  // Default: text/number input
  return (
    <Input
      type={["Int", "Float", "Currency"].includes(field.fieldtype) ? "number" : "text"}
      value={String(filter.value ?? "")}
      onChange={(e) => {
        const val = e.target.value;
        if (["Int", "Float", "Currency"].includes(field.fieldtype)) {
          onChange(val ? Number(val) : null);
        } else {
          onChange(val || null);
        }
      }}
      placeholder={`Enter ${field.label}`}
      className="w-[200px]"
    />
  );
}

